import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { AppContext } from "../context/appContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faUsers, faFingerprint } from "@fortawesome/free-solid-svg-icons";

// Master-data refs (religion / caste / location) and prefs come back POPULATED as
// objects from getMatchSeekerById. Resolve the human-readable field so an object
// never reaches JSX — React throws "Objects are not valid as a React child".
// Handles object | string | number | {min,max} range | null uniformly.
const displayValue = (v) => {
  if (v == null) return "";
  if (typeof v !== "object") return v;
  if (v.min != null || v.max != null) return `${v.min ?? "?"} - ${v.max ?? "?"}`;
  return v.religion ?? v.caste ?? v.city ?? v.location ?? v.label ?? v.name ?? "";
};

const Field = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <div className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-md border border-gray-100">
      {displayValue(value) || "N/A"}
    </div>
  </div>
);

const ViewMatchSeekerDetails = () => {
  const { id } = useParams();
  const [matchSeeker, setMatchSeeker] = useState(null);
  const [error, setError] = useState(null);
  const { matchseekerMasterData } = useContext(AppContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMatchSeekerDetails = async () => {
      setError(null);
      try {
        const response = await func.get(urls.GET_MATCHSEEKER_BY_ID_URL + id);
        const data = response?.status === 200 ? response.data?.data : null;
        if (!data) {
          setError("Could not load this match seeker. Please try again.");
          return;
        }
        setMatchSeeker({
          fullName: data.fullName || "N/A",
          emailId: data.emailId || "N/A",
          phoneNumber: data.userId?._phoneNumber || "N/A",
          accountStatus: data.userId?._accountStatus || "N/A",
          religion: data.religion?.label || "N/A",
          caste: data.caste?.label || "N/A",
          subCaste: data.subCaste || "N/A",
          createdAt: data?.createdAt
            ? new Date(data.createdAt).toLocaleDateString("en-IN")
            : "N/A",
          updatedAt: data?.updatedAt
            ? new Date(data.updatedAt).toLocaleDateString("en-IN")
            : "N/A",
          preferences: {
            religion: data.preferences?.religion?.label,
            caste: data.preferences?.caste?.label,
            location: data.preferences?.locationPreferences?.label,
            ageRange: data.preferences?.ageRange,
          },
        });
      } catch (e) {
        setError("Could not load this match seeker. Please try again.");
      }
    };

    if (id) fetchMatchSeekerDetails();
  }, [id, matchseekerMasterData]);

  if (error) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8 text-center space-y-4">
          <p className="text-sm text-gray-600">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!matchSeeker) {
    return (
      <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-6 animate-pulse space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            </button>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faUsers} className="text-white text-lg" />
              <h1 className="text-lg font-semibold text-white">Match Seeker Details</h1>
            </div>
          </div>
          <button
            onClick={() => navigate(`/user-deep-dive/${id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition"
          >
            <FontAwesomeIcon icon={faFingerprint} className="text-sm" />
            Deep Dive
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          {/* Personal Details */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Full Name" value={matchSeeker.fullName} />
              <Field label="Email" value={matchSeeker.emailId} />
              <Field label="Mobile Number" value={matchSeeker.phoneNumber} />
              <Field label="Religion" value={matchSeeker.religion} />
              <Field label="Caste" value={matchSeeker.caste} />
              <Field label="Sub Caste" value={matchSeeker.subCaste} />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Account Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Account Status" value={matchSeeker.accountStatus} />
              <Field label="Created Date" value={matchSeeker.createdAt} />
              <Field label="Updated Date" value={matchSeeker.updatedAt} />
            </div>
          </div>

          {/* Preferences */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Preferences
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Preferred Religion" value={matchSeeker.preferences?.religion} />
              <Field label="Preferred Caste" value={matchSeeker.preferences?.caste} />
              <Field label="Preferred Location" value={matchSeeker.preferences?.location} />
              <Field label="Preferred Age" value={matchSeeker.preferences?.ageRange} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewMatchSeekerDetails;
